import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HiveCtx = createContext({ online: false, alerts: [], hiveAlert: false, latest: null, dismiss: () => {} });
export const useHive = () => useContext(HiveCtx);

const SEEN_KEY = "octon_hive_seen";
const loadSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); } catch { return new Set(); }
};
const saveSeen = (s) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...s].slice(-300))); } catch { /* ignore */ }
};

const alertLabel = (e) => {
  const p = e.payload || {};
  return p.title || p.message || p.name || p.kind || e.type;
};

export function HiveProvider({ children }) {
  const { user } = useAuth();
  const [online, setOnline] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [hiveAlert, setHiveAlert] = useState(false);
  const [latest, setLatest] = useState(null);
  const seenRef = useRef(loadSeen());
  const firstLoad = useRef(true);
  const alertTimer = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/api/hive/alerts");
      setOnline(!!data.online);
      const list = data.alerts || [];
      setAlerts(list);
      if (list[0]) setLatest(list[0]);
      const unseen = list.filter((e) => e.hive_id && !seenRef.current.has(e.hive_id));
      if (unseen.length) {
        unseen.forEach((e) => seenRef.current.add(e.hive_id));
        saveSeen(seenRef.current);
        if (!firstLoad.current) {
          unseen.slice(0, 3).forEach((e) =>
            toast.warning(`OCTON HIVE · ${e.ship_from || "Fleet"}`, { description: alertLabel(e) })
          );
          setHiveAlert(true);
          clearTimeout(alertTimer.current);
          alertTimer.current = setTimeout(() => setHiveAlert(false), 45000);
        }
      }
      firstLoad.current = false;
    } catch { /* fleet unreachable — stay quiet */ }
  }, []);

  useEffect(() => {
    // Fleet alerts are internal operations data: admins only
    if (!user || user.role !== "admin") { setOnline(false); setAlerts([]); setHiveAlert(false); return; }
    poll();
    const id = setInterval(poll, 45000);
    return () => { clearInterval(id); clearTimeout(alertTimer.current); };
  }, [user, poll]);

  return (
    <HiveCtx.Provider value={{ online, alerts, hiveAlert, latest, dismiss: () => setHiveAlert(false) }}>
      {children}
    </HiveCtx.Provider>
  );
}
